import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  DragIndicator,
  KeyboardArrowDown,
  KeyboardArrowUp,
  MoreVert,
  Search,
} from '@mui/icons-material';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useSnackbar } from 'notistack';

import { DataAPIContext } from '../api';
import { GET_ONE, UPDATE_BATCH } from '../api/dataProvider';
import PageHeading from '../components/PageHeading';
import PageMeta from '../components/PageMeta';
import { ContextStore } from '../store/contextStore';

const TOUR_RESOURCE = 'tour';

const mapServerTour = (t) => ({
  id: t.id,
  name: t.name ?? '',
  custom_id: t.custom_id ?? null,
  start_date: t.start_date ?? null,
  chat_priority: Number(t.chat_priority ?? 0),
  exclude_from_chat: Boolean(t.exclude_from_chat),
});

const TourRow = React.memo(function TourRow({
  tour,
  index,
  canWrite,
  saving,
  searchActive,
  onOpenMenu,
}) {
  const dragDisabled = !canWrite || searchActive || saving;
  return (
    <Draggable
      draggableId={String(tour.id)}
      index={index}
      isDragDisabled={dragDisabled}
    >
      {(dragProvided, snapshot) => (
        <TableRow
          ref={dragProvided.innerRef}
          {...dragProvided.draggableProps}
          hover
          sx={{
            ...dragProvided.draggableProps.style,
            opacity: snapshot.isDragging ? 0.85 : 1,
            backgroundColor: snapshot.isDragging ? 'action.hover' : 'inherit',
          }}
        >
          <TableCell>
            <Box
              {...dragProvided.dragHandleProps}
              display="flex"
              alignItems="center"
              color={canWrite && !searchActive ? 'action.active' : 'action.disabled'}
            >
              <DragIndicator fontSize="small" />
            </Box>
          </TableCell>
          <TableCell>
            <Typography variant="body2">{tour.chat_priority}</Typography>
          </TableCell>
          <TableCell>
            <Typography variant="body2" fontWeight={500}>{tour.name}</Typography>
            {tour.custom_id && (
              <Typography variant="caption" color="text.secondary" display="block">
                {tour.custom_id}
              </Typography>
            )}
          </TableCell>
          <TableCell>
            <Typography variant="body2">{tour.start_date || '—'}</Typography>
          </TableCell>
          <TableCell>
            {tour.exclude_from_chat ? (
              <Chip label="Excluded from chat" size="small" color="warning" variant="outlined" />
            ) : (
              <Chip label="In chat" size="small" color="success" variant="outlined" />
            )}
          </TableCell>
          <TableCell align="right">
            <IconButton
              size="small"
              disabled={!canWrite || saving}
              onClick={(e) => onOpenMenu(e.currentTarget, tour.id)}
              aria-label="Tour actions"
            >
              <MoreVert fontSize="small" />
            </IconButton>
          </TableCell>
        </TableRow>
      )}
    </Draggable>
  );
});

const SetPriorityDialog = React.memo(function SetPriorityDialog({
  tour,
  maxPriority,
  onClose,
  onSubmit,
}) {
  const [value, setValue] = useState('');

  useEffect(() => {
    if (tour) setValue(String(tour.chat_priority));
  }, [tour]);

  const submit = () => {
    const parsed = parseInt(value, 10);
    onSubmit(parsed);
  };

  return (
    <Dialog open={Boolean(tour)} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Set priority</DialogTitle>
      <DialogContent>
        {tour && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {tour.name}
            </Typography>
            <TextField
              autoFocus
              fullWidth
              size="small"
              type="number"
              label="Priority"
              helperText={`1 (top) – ${maxPriority} (bottom)`}
              inputProps={{ min: 1, max: maxPriority }}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={submit} variant="contained">Apply</Button>
      </DialogActions>
    </Dialog>
  );
});

const TourChatPriorityPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  const dataAPI = useContext(DataAPIContext);
  const { hasPerm, tourOperator } = useContext(ContextStore);
  const featureEnabled = Boolean(tourOperator?.config?.enable_tour_chat_priority);
  const canView = featureEnabled && hasPerm('tour', 'read');
  const canWrite = featureEnabled && hasPerm('tour', 'write');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tours, setTours] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuTourId, setMenuTourId] = useState(null);
  const [priorityDialogTour, setPriorityDialogTour] = useState(null);

  const fetchTours = useCallback(async () => {
    if (!canView) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await dataAPI?.(GET_ONE, TOUR_RESOURCE, { urlSuffix: 'chat_priorities/' });
      const rows = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      setTours(rows.map(mapServerTour));
    } catch {
      enqueueSnackbar('Failed to load tours', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [dataAPI, enqueueSnackbar, canView]);

  useEffect(() => {
    fetchTours();
  }, [fetchTours]);

  const sortedTours = useMemo(
    () => [...tours].sort((a, b) => a.chat_priority - b.chat_priority || a.id - b.id),
    [tours]
  );

  const filteredTours = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return sortedTours;
    return sortedTours.filter((t) => {
      if (t.name?.toLowerCase().includes(q)) return true;
      if (t.custom_id?.toLowerCase().includes(q)) return true;
      if (String(t.id).includes(q)) return true;
      return false;
    });
  }, [sortedTours, searchQuery]);

  const searchActive = Boolean(searchQuery.trim());

  const applyMove = useCallback(async (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    const clampedTo = Math.max(0, Math.min(sortedTours.length - 1, toIndex));
    if (clampedTo === fromIndex) return;

    const list = [...sortedTours];
    const [moved] = list.splice(fromIndex, 1);
    list.splice(clampedTo, 0, moved);

    const withNewPriority = list.map((t, idx) => ({ ...t, chat_priority: idx + 1 }));
    const changed = withNewPriority.filter(
      (t) => sortedTours.find((o) => o.id === t.id)?.chat_priority !== t.chat_priority
    );
    if (!changed.length) return;

    const priorityMap = new Map(withNewPriority.map((t) => [t.id, t.chat_priority]));
    setTours((prev) => prev.map((t) => (
      priorityMap.has(t.id) ? { ...t, chat_priority: priorityMap.get(t.id) } : t
    )));

    setSaving(true);
    try {
      await dataAPI?.(UPDATE_BATCH, TOUR_RESOURCE, {
        urlSuffix: 'chat_priorities/',
        data: {
          ordering: changed.map((t) => ({ id: t.id, chat_priority: t.chat_priority })),
        },
      });
    } catch {
      enqueueSnackbar('Failed to save order, reloading…', { variant: 'error' });
      fetchTours();
    } finally {
      setSaving(false);
    }
  }, [sortedTours, dataAPI, enqueueSnackbar, fetchTours]);

  const handleDragEnd = async (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    if (!canWrite) {
      enqueueSnackbar('You do not have permission to reorder tours', { variant: 'warning' });
      return;
    }
    if (searchActive) {
      enqueueSnackbar('Clear the search box to reorder tours', { variant: 'info' });
      return;
    }
    await applyMove(result.source.index, result.destination.index);
  };

  const openRowMenu = useCallback((anchorEl, tourId) => {
    setMenuAnchor(anchorEl);
    setMenuTourId(tourId);
  }, []);

  const closeRowMenu = useCallback(() => {
    setMenuAnchor(null);
    setMenuTourId(null);
  }, []);

  const currentIndexOf = (tourId) => sortedTours.findIndex((t) => t.id === tourId);

  const handleMoveToTop = async () => {
    const idx = currentIndexOf(menuTourId);
    closeRowMenu();
    if (idx > 0) await applyMove(idx, 0);
  };

  const handleMoveToBottom = async () => {
    const idx = currentIndexOf(menuTourId);
    closeRowMenu();
    if (idx >= 0 && idx < sortedTours.length - 1) {
      await applyMove(idx, sortedTours.length - 1);
    }
  };

  const handleOpenSetPriority = () => {
    const tour = sortedTours.find((t) => t.id === menuTourId);
    if (tour) setPriorityDialogTour(tour);
    closeRowMenu();
  };

  const closePriorityDialog = useCallback(() => setPriorityDialogTour(null), []);

  const handleSubmitPriority = useCallback(async (parsed) => {
    const tour = priorityDialogTour;
    if (!tour) return;
    if (Number.isNaN(parsed) || parsed < 1) {
      enqueueSnackbar(`Enter a number between 1 and ${sortedTours.length}`, { variant: 'warning' });
      return;
    }
    const fromIndex = sortedTours.findIndex((t) => t.id === tour.id);
    const toIndex = parsed - 1;
    setPriorityDialogTour(null);
    if (fromIndex >= 0) await applyMove(fromIndex, toIndex);
  }, [priorityDialogTour, sortedTours, applyMove, enqueueSnackbar]);

  if (!canView) {
    return (
      <>
        <PageMeta title="Tour chat priority" />
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6">Access denied</Typography>
          <Typography>
            {!featureEnabled
              ? 'Tour chat priority is not enabled for this tour operator.'
              : 'You do not have permission to view tour chat priority.'}
          </Typography>
        </Paper>
      </>
    );
  }

  return (
    <>
      <PageMeta title="Tour chat priority" />
      <Box sx={{ px: { xs: 1, sm: 2 }, py: 2 }}>
        <PageHeading sx={{ mb: 0.5 }}>Tour chat priority</PageHeading>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Drag tours to set the order they appear in chat. Higher items have higher priority.
        </Typography>

        <Paper variant="outlined" sx={{ p: 2, borderRadius: '12px' }}>
          <Box
            display="flex"
            flexDirection={isMobile ? 'column' : 'row'}
            alignItems={isMobile ? 'stretch' : 'center'}
            justifyContent="space-between"
            gap={1.5}
            mb={2}
          >
            <TextField
              size="small"
              placeholder="Search tours…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: isMobile ? '100%' : 280 }}
            />
            {saving && <CircularProgress size={22} />}
          </Box>

          {canWrite && searchActive && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Drag-to-reorder is disabled while filtering. Clear the search to drag, or use the
              row menu to move a tour to the top, bottom, or a specific priority.
            </Alert>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 48 }} />
                    <TableCell sx={{ width: 72 }}>Priority</TableCell>
                    <TableCell>Tour</TableCell>
                    <TableCell sx={{ width: 140 }}>Start date</TableCell>
                    <TableCell sx={{ width: 160 }}>Status</TableCell>
                    <TableCell sx={{ width: 56 }} align="right" />
                  </TableRow>
                </TableHead>
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="tour-chat-priority">
                    {(provided) => (
                      <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                        {filteredTours.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                              <Typography color="text.secondary">
                                {searchActive ? 'No tours match your search' : 'No tours found'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredTours.map((tour, index) => (
                            <TourRow
                              key={tour.id}
                              tour={tour}
                              index={index}
                              canWrite={canWrite}
                              saving={saving}
                              searchActive={searchActive}
                              onOpenMenu={openRowMenu}
                            />
                          ))
                        )}
                        {provided.placeholder}
                      </TableBody>
                    )}
                  </Droppable>
                </DragDropContext>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeRowMenu}>
        <MenuItem
          onClick={handleMoveToTop}
          disabled={menuTourId == null || currentIndexOf(menuTourId) === 0}
        >
          <KeyboardArrowUp fontSize="small" sx={{ mr: 1 }} />
          Move to top
        </MenuItem>
        <MenuItem
          onClick={handleMoveToBottom}
          disabled={menuTourId == null || currentIndexOf(menuTourId) === sortedTours.length - 1}
        >
          <KeyboardArrowDown fontSize="small" sx={{ mr: 1 }} />
          Move to bottom
        </MenuItem>
        <MenuItem onClick={handleOpenSetPriority} disabled={menuTourId == null}>
          Set priority…
        </MenuItem>
      </Menu>

      <SetPriorityDialog
        tour={priorityDialogTour}
        maxPriority={sortedTours.length}
        onClose={closePriorityDialog}
        onSubmit={handleSubmitPriority}
      />
    </>
  );
};

export default TourChatPriorityPage;
